import { useState, useRef } from 'react';
import { useSkills } from '../../lib/use-skills';
import { Lightbulb, Plus, Edit, Trash2, Upload, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { SkillZipImportDialog } from './SkillZipImportDialog';
import { SectionHeader } from '../ui/section-header';
import { SkillConfigForm } from './SkillConfigForm';
import type { Skill } from '../../lib/skill-types';

interface SkillListProps {
  workspaceId: string | null;
}

export function SkillList({ workspaceId }: SkillListProps) {
  const { skills, loading, deleteSkill, importSkill, importSkillZip, createSkill, updateSkill, loadSkills } = useSkills(workspaceId);
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editTarget, setEditTarget] = useState<Skill | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (view === 'create') {
    return (
      <SkillConfigForm
        workspaceId={workspaceId}
        onBack={() => setView('list')}
        onSave={async (data) => {
          await createSkill(data as any);
          await loadSkills();
          setView('list');
        }}
      />
    );
  }

  if (view === 'edit' && editTarget) {
    return (
      <SkillConfigForm
        skill={editTarget}
        workspaceId={workspaceId}
        onBack={() => { setView('list'); setEditTarget(null); }}
        onSave={async (data) => {
          await updateSkill(editTarget.id, data as any);
          await loadSkills();
          setView('list');
          setEditTarget(null);
        }}
      />
    );
  }

  const handleDelete = async (skill: Skill) => {
    // First click: show inline confirmation
    if (confirmDeleteId !== skill.id) {
      setConfirmDeleteId(skill.id);
      return;
    }

    // Second click: actually delete
    setConfirmDeleteId(null);
    setDeletingId(skill.id);
    try {
      await deleteSkill(skill.id);
      await loadSkills();
    } catch (error) {
      console.error('Failed to delete skill:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setImporting(true);
    try {
      const selectedZipFile = Array.from(files).find(f => f.name.toLowerCase().endsWith('.zip'));

      if (selectedZipFile) {
        setZipFile(selectedZipFile);
        setImporting(false);
        return;
      } else {
        const fileContents: Record<string, string> = {};
        let skillName = 'Imported Skill';

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const content = await file.text();
          fileContents[file.name] = content;

          if (file.name.toLowerCase() === 'skill.md') {
            const nameMatch = content.match(/^name:\s*(.+)$/im);
            if (nameMatch) {
              skillName = nameMatch[1].trim().replace(/^["']|["']$/g, '');
            }
          }
        }

        if (!fileContents['SKILL.md'] && !fileContents['skill.md']) {
          alert('Import must include a SKILL.md file');
          return;
        }

        await importSkill({ skillName, files: fileContents });
        await loadSkills();
        alert(`Skill "${skillName}" imported successfully!`);
      }
    } catch (error) {
      console.error('Failed to import skill:', error);
      alert('Failed to import skill. Please try again.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SectionHeader
        icon={<Lightbulb className="size-4 text-primary" />}
        title="Skills"
        subtitle="Extend agent capabilities"
        footer={
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {skills.length} skill{skills.length !== 1 ? 's' : ''}
            </p>
            <div className="flex gap-1">
              <Button
                onClick={() => fileInputRef.current?.click()}
                size="sm"
                variant="outline"
                disabled={importing}
                className="h-7 px-2 text-xs"
              >
              {importing ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Upload className="size-3.5" />
              )}
              <span className="hidden min-[340px]:inline ml-1">{importing ? 'Importing...' : 'Import'}</span>
            </Button>
            <Button onClick={() => setView('create')} size="sm" className="h-7 px-2 text-xs">
              <Plus className="size-3.5" />
              <span className="hidden min-[340px]:inline ml-1">New Skill</span>
            </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".md,.txt,.json,.zip"
                onChange={handleImport}
                className="hidden"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="size-6 text-primary animate-spin mb-3" />
            <p className="text-sm text-muted-foreground">Loading skills...</p>
          </div>
        ) : skills.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="flex items-center justify-center size-14 rounded-2xl bg-muted mb-4">
              <Lightbulb className="size-7 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium mb-1">No skills yet</p>
            <p className="text-xs text-muted-foreground mb-5 text-center max-w-[240px]">
              Create or import a skill to extend your agent's capabilities.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                size="sm"
                disabled={importing}
              >
                <Upload className="size-3.5 mr-1.5" />
                Import
              </Button>
              <Button onClick={() => setView('create')} size="sm">
                <Plus className="size-3.5 mr-1.5" />
                Create Skill
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {skills.map((skill) => (
              <div
                key={skill.id}
                className="group border rounded-lg bg-card p-3.5 transition-colors hover:bg-accent/40"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center size-8 rounded-md bg-muted shrink-0">
                    <Lightbulb className="size-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex flex-col items-start justify-center gap-1 min-w-0">
                      <span className="text-sm font-medium truncate max-w-full">{skill.skillName}</span>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {skill.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {confirmDeleteId === skill.id ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDeleteId(null)}
                          className="h-7 px-2 text-xs text-muted-foreground"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(skill)}
                          disabled={deletingId === skill.id}
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          {deletingId === skill.id ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            'Delete'
                          )}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditTarget(skill); setView('edit'); }}
                          disabled={deletingId === skill.id}
                          className="h-7 w-7 p-0"
                        >
                          <Edit className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(skill)}
                          disabled={deletingId === skill.id}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        >
                          {deletingId === skill.id ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="size-3.5" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {zipFile && (
        <SkillZipImportDialog
          file={zipFile}
          onClose={() => {
            setZipFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
          onConfirm={async (f, name) => {
            await importSkillZip(f, name);
            await loadSkills();
            setZipFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      )}
    </div>
  );
}
